import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";
import {
  COMPANY_INFO,
  AI_SERVICES,
  SERVICE_BUNDLES,
  INDIVIDUAL_SERVICES,
  INDUSTRIES,
  RESOURCES_MENU,
  PRODUCTS_MENU,
  ABOUT_MENU,
  ENGAGEMENT_MODELS,
} from "../../data/mock";
import screenqubeImage from "../../assets/team/screencube.jpg";
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Navigation structure
  const navigation = [
    {
      name: "AI",
      path: "/ai",
      submenu: AI_SERVICES.map((s) => ({
        name: s.title,
        path: `/ai/${s.slug}`,
        shortDesc: s.shortDesc,
        image: s.image,
      })),
      menuType: "compact", // Like About Us
    },
    {
      name: "What We Do",
      path: "/what-we-do",
      submenu: null,
      menuType: "twoColumn",
      columns: {
        left: {
          title: "Service Bundles",
          items: SERVICE_BUNDLES.map((s) => ({
            name: s.title,
            path: `/what-we-do/${s.slug}`,
            shortDesc: s.shortDesc,
            image: s.image,
          })),
        },
        leftBottom: {
          title: "Engagement Model",
          items: ENGAGEMENT_MODELS.map((e) => ({
            name: e.title,
            path: `/what-we-do/engagement-model#${e.anchor}`,
            shortDesc: e.shortDesc,
            image: e.image,
          })),
        },
        right: {
          title: "Individual Services",
          items: INDIVIDUAL_SERVICES.map((s) => ({
            name: s.title,
            path: `/what-we-do/${s.slug}`,
            shortDesc: s.shortDesc,
            image: s.image,
          })),
        },
      },
    },
    {
      name: "Industries",
      path: "/industries",
      submenu: INDUSTRIES.map((i) => ({
        name: i.title,
        path: `/industries/${i.slug}`,
        shortDesc: i.description,
        image: i.image,
      })),
      menuType: "wide",
    },
    {
      name: "Products",
      path: "/products",
        target: "_self",
      submenu: PRODUCTS_MENU.map((r) => ({
        name: r.title,
        path: r.path,
        shortDesc: r.shortDesc,
        image: screenqubeImage,
      })),
      menuType: "compact",
    },
    {
      name: "Resources",
      path: "/resources",
      submenu: RESOURCES_MENU.map((r) => ({
        name: r.title,
        path: r.path,
        shortDesc: r.shortDesc,
        image: r.image,
      })),
      menuType: "compact",
    },
    {
      name: "About Us",
      path: "/about",
      submenu: ABOUT_MENU.map((a) => ({
        name: a.title,
        path: a.path,
        shortDesc: a.shortDesc,
        image: a.image,
      })),
      menuType: "compact",
    },
    { name: "Careers", path: "/careers", submenu: null },
  ];

  const getDefaultPreviewItem = (submenu) => {
    return submenu && submenu.length > 0 ? submenu[0] : null;
  };

  // Standard MegaMenu Component for single-column or wide layouts
  const MegaMenu = ({ item, isActive }) => {
    const [previewItem, setPreviewItem] = useState(
      getDefaultPreviewItem(item.submenu),
    );

    useEffect(() => {
      if (isActive && item.submenu) {
        setPreviewItem(getDefaultPreviewItem(item.submenu));
      }
    }, [isActive, item.submenu]);

    if (!item.submenu || !isActive) return null;

    const isWide = item.menuType === "wide";
    const menuWidth = isWide ? "1100px" : "750px";
    const leftWidth = isWide ? "60%" : "45%";
    const rightWidth = isWide ? "40%" : "55%";
    const gridCols = isWide ? "grid-cols-2" : "grid-cols-1";

    return (
      <div
        className="fixed z-50"
        style={{
          top: "64px",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
          style={{
            width: menuWidth,
            maxWidth: "calc(100vw - 40px)",
          }}
        >
          <div className="flex">
            {/* Left side - Menu Items */}
            <div
              className="bg-gray-50 p-4 max-h-[70vh] overflow-y-auto"
              style={{ width: leftWidth }}
            >
              <div className={`grid ${gridCols} gap-1`}>
                
{item.submenu.map((subItem) => {
  const isActive = previewItem?.path === subItem.path;

  const itemClass = `group flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 ${
    isActive
      ? "bg-white shadow-md text-[#b32126]"
      : "text-gray-700 hover:bg-white hover:shadow-sm hover:text-[#b32126]"
  }`;

  const content = (
    <>
      <span className="text-sm font-medium pr-2 whitespace-nowrap">
        {subItem.name}
      </span>

      <ChevronRight
        className={`h-4 w-4 flex-shrink-0 transition-transform ${
          isActive
            ? "translate-x-1"
            : "group-hover:translate-x-1"
        }`}
      />
    </>
  );

  return subItem.path?.startsWith("http") ? (
    <a
      key={subItem.path}
      href={subItem.path}
      target={subItem.target || "_blank"}
      rel={
        subItem.target === "_self"
          ? undefined
          : "noopener noreferrer"
      }
      className={itemClass}
      onMouseEnter={() => setPreviewItem(subItem)}
    >
      {content}
    </a>
  ) : (
    <Link
      key={subItem.path}
      to={subItem.path}
      className={itemClass}
      onMouseEnter={() => setPreviewItem(subItem)}
    >
      {content}
    </Link>
  );
})}

              </div>
            </div>

            {/* Right side - Preview Panel */}
            <div className="p-6 flex flex-col" style={{ width: rightWidth }}>
              {previewItem && (
                <div className="flex flex-col h-full animate-fadeIn">
                <div className="relative w-full h-44 rounded-xl overflow-hidden mb-4 bg-gray-100">
  {previewItem.path?.startsWith("https://") ? (
    <a
      href={previewItem.path}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full h-full cursor-pointer"
    >
      <img
        src={previewItem.image}
        alt={previewItem.name}
        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src =
            "https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?w=400&q=80";
        }}
      />
    </a>
  ) : (
    <img
      src={previewItem.image}
      alt={previewItem.name}
      className="w-full h-full object-cover"
    />
  )}

  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {previewItem.name}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                    {previewItem.shortDesc}
                  </p>
                  {previewItem.path?.startsWith("https://") ? (
  <a
    href={previewItem.path}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center text-[#b32126] font-semibold text-sm group mt-auto"
  >
    Learn More
    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
  </a>
) : (
  <Link
    to={previewItem.path}
    className="inline-flex items-center text-[#b32126] font-semibold text-sm group mt-auto"
  >
    Learn More
    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
  </Link>
)}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 border-t border-gray-100 px-6 py-3">
            <Link
              to={item.path}
              className="inline-flex items-center text-sm font-medium text-[#b32126] hover:text-[#8f1a1f] transition-colors"
            >
              View all {item.name.toLowerCase()}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // Two-Column MegaMenu for "What We Do"
  const TwoColumnMegaMenu = ({ item, isActive }) => {
    const [previewItem, setPreviewItem] = useState(
      item.columns?.left?.items?.[0] || null,
    );

    useEffect(() => {
      if (isActive && item.columns?.left?.items) {
        setPreviewItem(item.columns.left.items[0]);
      }
    }, [isActive, item.columns]);

    if (!item.columns || !isActive) return null;

    return (
      <div
        className="fixed z-50"
        style={{
          top: "64px",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
          style={{
            width: "1100px",
            maxWidth: "calc(100vw - 40px)",
          }}
        >
          <div className="flex">
            {/* Left Column - Service Bundles + Engagement Model */}
            <div className="w-1/4 bg-gray-50 p-4 border-r border-gray-100 max-h-[70vh] overflow-y-auto">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-4">
                {item.columns.left.title}
              </h3>
              <div className="space-y-1">
                {item.columns.left.items.map((subItem) => (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`group flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-200 ${
                      previewItem?.path === subItem.path
                        ? "bg-white shadow-md text-[#b32126]"
                        : "text-gray-700 hover:bg-white hover:shadow-sm hover:text-[#b32126]"
                    }`}
                    onMouseEnter={() => setPreviewItem(subItem)}
                  >
                    <span className="text-sm font-medium">{subItem.name}</span>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
                  </Link>
                ))}
              </div>

              {/* Engagement Model Section */}
              {item.columns.leftBottom && (
                <>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 mt-6 px-4">
                    {item.columns.leftBottom.title}
                  </h3>
                  <div className="space-y-1">
                    {item.columns.leftBottom.items.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        className={`group flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-200 ${
                          previewItem?.path === subItem.path
                            ? "bg-white shadow-md text-[#b32126]"
                            : "text-gray-700 hover:bg-white hover:shadow-sm hover:text-[#b32126]"
                        }`}
                        onMouseEnter={() => setPreviewItem(subItem)}
                      >
                        <span className="text-sm font-medium">
                          {subItem.name}
                        </span>
                        <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Middle Column - Individual Services */}
            <div className="w-2/5 bg-gray-50 p-4 max-h-[70vh] overflow-y-auto">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-4">
                {item.columns.right.title}
              </h3>
              <div className="space-y-1">
                {item.columns.right.items.map((subItem) => (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`group flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-200 ${
                      previewItem?.path === subItem.path
                        ? "bg-white shadow-md text-[#b32126]"
                        : "text-gray-700 hover:bg-white hover:shadow-sm hover:text-[#b32126]"
                    }`}
                    onMouseEnter={() => setPreviewItem(subItem)}
                  >
                    <span className="text-sm font-medium">{subItem.name}</span>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Right side - Preview Panel */}
            <div className="w-[35%] p-6 flex flex-col">
              {previewItem && (
                <div className="flex flex-col h-full animate-fadeIn">
                  <div className="relative w-full h-40 rounded-xl overflow-hidden mb-4 bg-gray-100">
                    <img
                      src={previewItem.image}
                      alt={previewItem.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      onError={(e) => {
                        e.target.src =
                          "https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?w=400&q=80";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {previewItem.name}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                    {previewItem.shortDesc}
                  </p>
                  <Link
                    to={previewItem.path}
                    className="inline-flex items-center text-[#b32126] font-semibold text-sm group mt-auto"
                  >
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 border-t border-gray-100 px-6 py-3">
            <Link
              to={item.path}
              className="inline-flex items-center text-sm font-medium text-[#b32126] hover:text-[#8f1a1f] transition-colors"
            >
              View all services
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Menu Item Component
  const MobileMenuItem = ({ item }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedSubItem, setSelectedSubItem] = useState(null);

    const hasSubmenu = item.submenu || item.columns;

    const getAllItems = () => {
      if (item.submenu) return item.submenu;
      if (item.columns) {
        return [
          { isHeader: true, title: item.columns.left.title },
          ...item.columns.left.items,
          { isHeader: true, title: item.columns.right.title },
          ...item.columns.right.items,
        ];
      }
      return [];
    };

    return (
      <div className="border-b border-gray-100 last:border-b-0">
        <div
          className="flex items-center justify-between px-4 py-4 text-gray-700 hover:text-[#b32126] hover:bg-red-50 cursor-pointer"
          onClick={() => {
            if (hasSubmenu) {
              setIsExpanded(!isExpanded);
              setSelectedSubItem(null);
            }
          }}
        >
          <Link
            to={item.path}
            className="flex-1 font-medium text-base"
            onClick={(e) => hasSubmenu && e.preventDefault()}
          >
            {item.name}
          </Link>
          {hasSubmenu && (
            <ChevronDown
              className={`h-5 w-5 transition-transform duration-300 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          )}
        </div>

        {hasSubmenu && isExpanded && (
          <div className="bg-gray-50 pb-4">
            {selectedSubItem && (
              <div className="mx-4 mb-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 animate-fadeIn">
                <div className="flex gap-4">
                  <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={selectedSubItem.image}
                      alt={selectedSubItem.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src =
                          "https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?w=400&q=80";
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">
                      {selectedSubItem.name}
                    </h4>
                    <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                      {selectedSubItem.shortDesc}
                    </p>
                    <Link
                      to={selectedSubItem.path}
                      className="inline-flex items-center text-xs font-semibold text-[#b32126]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Learn More <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className="px-4 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {getAllItems().map((subItem, index) =>
                subItem.isHeader ? (
                  <div
                    key={`header-${index}`}
                    className="col-span-full px-3 py-2 mt-2 first:mt-0"
                  >
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      {subItem.title}
                    </span>
                  </div>
                ) : (
                  <div
                    key={subItem.path}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                      selectedSubItem?.path === subItem.path
                        ? "bg-white shadow-sm text-[#b32126]"
                        : "text-gray-600 hover:bg-white hover:text-[#b32126]"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSubItem(subItem);
                    }}
                  >
                    <span className="text-sm font-medium">{subItem.name}</span>
                    <ChevronRight className="h-4 w-4 flex-shrink-0" />
                  </div>
                ),
              )}
            </div>

            <div className="px-4 mt-3">
              <Link
                to={item.path}
                className="inline-flex items-center text-sm font-medium text-[#b32126] hover:text-[#8f1a1f]"
                onClick={() => setMobileMenuOpen(false)}
              >
                View all {item.name.toLowerCase()}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white shadow-lg py-2"
          : "bg-white/95 backdrop-blur-sm py-4"
      }`}
      onMouseLeave={() => setActiveDropdown(null)}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.2s ease-out forwards;
          }
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}
      </style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={COMPANY_INFO.logo}
              alt={COMPANY_INFO.name}
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-1">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => {
                  if (item.submenu || item.columns) {
                    setActiveDropdown(item.name);
                  } else {
                    setActiveDropdown(null); // close dropdown for non-menu items
                  }
                }}
              >
                <Link
                  to={item.path}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === item.path ||
                    location.pathname.startsWith(item.path + "/")
                      ? "text-[#b32126] bg-red-50"
                      : "text-gray-700 hover:text-[#b32126] hover:bg-red-50"
                  }`}
                >
                  {item.name}
                  {(item.submenu || item.columns) && (
                    <ChevronDown
                      className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                        activeDropdown === item.name ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>

                {/* Render appropriate menu type */}
                {item.menuType === "twoColumn" ? (
                  <TwoColumnMegaMenu
                    item={item}
                    isActive={activeDropdown === item.name}
                  />
                ) : item.submenu ? (
                  <MegaMenu
                    item={item}
                    isActive={activeDropdown === item.name}
                  />
                ) : null}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden xl:flex">
            <Link
              to="/contact"
              className="inline-flex items-center px-5 py-2.5 bg-[#b32126] text-white text-sm font-semibold rounded-lg hover:bg-[#8f1a1f] transition-colors shadow-md hover:shadow-lg"
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden p-2 rounded-lg text-gray-600 hover:text-[#b32126] hover:bg-red-50"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden absolute left-0 right-0 top-full bg-white shadow-2xl border-t border-gray-100 max-h-[calc(100vh-64px)] overflow-y-auto z-50">
            <nav className="divide-y divide-gray-100">
              {navigation.map((item) => (
                <MobileMenuItem key={item.name} item={item} />
              ))}
            </nav>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <Link
                to="/contact"
                className="block w-full px-6 py-3.5 bg-[#b32126] text-white text-center font-semibold rounded-xl shadow-md hover:bg-[#8f1a1f] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
